import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import DeleteConfirmDialog from "../../components/DeleteConfirmDialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Truck, Search, Phone, Mail, User, Settings, Tag, MapPin, TrendingUp, ShoppingCart, CalendarDays, Loader2, Sparkles, FileText } from "lucide-react";
import SistemaOrfaniDialog from "../../components/acquisti/SistemaOrfaniDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Fornitori = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, locali } = useAuth();
  const [items, setItems] = useState([]);
  const [tipologie, setTipologie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [spesaStats, setSpesaStats] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroTipologia, setFiltroTipologia] = useState("all");
  const [form, setForm] = useState({ 
    ragione_sociale: "", 
    piva: "",
    categoria: "",  // "food" | "packaging"
    tipologia_id: "",
    contatto_nome: "", 
    contatto_email: "", 
    contatto_telefono: "",
    note: "",
    locali_ids: []
  });
  const [deleteId, setDeleteId] = useState(null);
  
  // VIES auto-fill state
  const [viesLoading, setViesLoading] = useState(false);
  const [viesLastTried, setViesLastTried] = useState("");
  
  // Gestione tipologie
  const [tipologiaDialogOpen, setTipologiaDialogOpen] = useState(false);
  const [tipologiaForm, setTipologiaForm] = useState({ nome: "", icona: "📦" });
  const [editTipologia, setEditTipologia] = useState(null);
  const [deleteTipologiaId, setDeleteTipologiaId] = useState(null);

  // Dialog "Sistema orfani"
  const [orfaniDialogOpen, setOrfaniDialogOpen] = useState(false);
  const [orfaniCount, setOrfaniCount] = useState(0);

  const fetchOrfaniCount = async () => {
    try {
      const res = await axios.get(`${API}/fornitori/orfani-fix`, { headers: getAuthHeaders() });
      setOrfaniCount((res.data.orfani || []).length);
    } catch {
      // silenzioso
    }
  };

  const fetchData = async () => {
    try {
      const [fornitoriRes, tipologieRes, statsRes] = await Promise.all([
        axios.get(`${API}/fornitori`, { headers: getAuthHeaders() }),
        axios.get(`${API}/tipologie-fornitore`, { headers: getAuthHeaders() }),
        axios.get(`${API}/fornitori/stats`, { headers: getAuthHeaders() })
      ]);
      setItems(fornitoriRes.data);
      setTipologie(tipologieRes.data);
      setSpesaStats(statsRes.data || {});
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchOrfaniCount();
  }, []);

  const openDialog = (item = null) => {
    if (item) {
      // Se la tipologia salvata è orfana (cancellata), resetta a vuoto così il select
      // mostra il placeholder invece di un valore fantasma non selezionabile.
      const tipologieIdsValid = new Set(tipologie.map(t => t.id));
      const tipIdValid = item.tipologia_id && tipologieIdsValid.has(item.tipologia_id) ? item.tipologia_id : "";
      setEditItem(item);
      setForm({ 
        ragione_sociale: item.ragione_sociale || "", 
        piva: item.piva || "",
        categoria: item.categoria || "",
        tipologia_id: tipIdValid,
        contatto_nome: item.contatto_nome || "", 
        contatto_email: item.contatto_email || "", 
        contatto_telefono: item.contatto_telefono || "",
        note: item.note || "",
        locali_ids: item.locali_ids || []
      });
    } else {
      setEditItem(null);
      // Se c'è un solo locale, selezionalo automaticamente
      const defaultLocaliIds = locali && locali.length === 1 ? [locali[0].id] : [];
      setForm({ ragione_sociale: "", piva: "", categoria: "", tipologia_id: "", contatto_nome: "", contatto_email: "", contatto_telefono: "", note: "", locali_ids: defaultLocaliIds });
    }
    setDialogOpen(true);
    setViesLastTried("");
  };

  // VIES auto-fill: chiama il backend per ottenere dati anagrafici da P.IVA
  const lookupPiva = async (pivaToLookup, { silentOnEmpty = false } = {}) => {
    const clean = (pivaToLookup || "").replace(/[\s.\-]/g, "");
    if (!clean) {
      if (!silentOnEmpty) toast.error("Inserisci una P.IVA");
      return;
    }
    // P.IVA IT = 11 cifre. Per altri paesi UE accettiamo prefisso lettere.
    const hasCountryPrefix = /^[A-Za-z]{2}/.test(clean);
    const digitsOnly = clean.replace(/^[A-Za-z]{2}/, "");
    if (!hasCountryPrefix && digitsOnly.length !== 11) {
      if (!silentOnEmpty) toast.error("La P.IVA italiana deve essere di 11 cifre");
      return;
    }
    setViesLoading(true);
    setViesLastTried(clean);
    try {
      const { data } = await axios.get(`${API}/fornitori/lookup-piva/${encodeURIComponent(clean)}`, {
        headers: getAuthHeaders(),
      });
      // Auto-compila i campi vuoti
      setForm((prev) => ({
        ...prev,
        ragione_sociale: prev.ragione_sociale?.trim() ? prev.ragione_sociale : (data.ragione_sociale || prev.ragione_sociale),
        note: data.indirizzo && !prev.note ? data.indirizzo : prev.note,
      }));
      if (data.ragione_sociale) {
        toast.success(data.cached ? "Dati VIES caricati (cache)" : "Dati VIES caricati");
      } else {
        toast.message("P.IVA valida, ma il VIES non ha restituito anagrafica");
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Errore lookup P.IVA";
      if (err.response?.status === 404) {
        toast.error("P.IVA non trovata nel VIES — inserisci i dati manualmente");
      } else if (err.response?.status === 503) {
        toast.error("Servizio VIES non disponibile — inserisci i dati manualmente");
      } else {
        toast.error(detail);
      }
    } finally {
      setViesLoading(false);
    }
  };

  // Debounced auto-lookup: scatta quando P.IVA = 11 cifre numeriche
  useEffect(() => {
    if (!dialogOpen) return;
    const clean = (form.piva || "").replace(/[\s.\-]/g, "");
    if (clean.length !== 11 || !/^\d{11}$/.test(clean)) return;
    if (clean === viesLastTried) return;
    // Skip in modifica se il fornitore esiste già con una ragione sociale
    if (editItem && form.ragione_sociale?.trim()) return;
    const t = setTimeout(() => {
      lookupPiva(clean, { silentOnEmpty: true });
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.piva, dialogOpen]);

  const toggleLocale = (localeId) => {
    setForm(prev => ({
      ...prev,
      locali_ids: prev.locali_ids.includes(localeId)
        ? prev.locali_ids.filter(id => id !== localeId)
        : [...prev.locali_ids, localeId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.ragione_sociale) {
      toast.error("Inserisci la ragione sociale");
      return;
    }

    try {
      const payload = {
        ...form,
        tipologia_id: form.tipologia_id || null
      };
      
      if (editItem) {
        await axios.put(`${API}/fornitori/${editItem.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Fornitore aggiornato");
      } else {
        await axios.post(`${API}/fornitori`, payload, { headers: getAuthHeaders() });
        toast.success("Fornitore creato");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/fornitori/${deleteId}`, { headers: getAuthHeaders() });
      toast.success("Fornitore eliminato");
      setDeleteId(null);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Tipologie handlers
  const openTipologiaDialog = (tip = null) => {
    if (tip) {
      setEditTipologia(tip);
      setTipologiaForm({ nome: tip.nome, icona: tip.icona });
    } else {
      setEditTipologia(null);
      setTipologiaForm({ nome: "", icona: "📦" });
    }
    setTipologiaDialogOpen(true);
  };

  const handleTipologiaSubmit = async (e) => {
    e.preventDefault();
    if (!tipologiaForm.nome) {
      toast.error("Inserisci il nome");
      return;
    }
    try {
      if (editTipologia) {
        await axios.put(`${API}/tipologie-fornitore/${editTipologia.id}`, tipologiaForm, { headers: getAuthHeaders() });
        toast.success("Tipologia aggiornata");
      } else {
        await axios.post(`${API}/tipologie-fornitore`, tipologiaForm, { headers: getAuthHeaders() });
        toast.success("Tipologia creata");
      }
      setTipologiaDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDeleteTipologia = async () => {
    if (!deleteTipologiaId) return;
    try {
      await axios.delete(`${API}/tipologie-fornitore/${deleteTipologiaId}`, { headers: getAuthHeaders() });
      toast.success("Tipologia eliminata");
      setDeleteTipologiaId(null);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Helper per tipologia
  const getTipologia = (id) => tipologie.find(t => t.id === id);

  const filteredItems = items.filter(item => {
    const matchSearch = item.ragione_sociale.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.contatto_nome && item.contatto_nome.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchTipologia = filtroTipologia === "all" || item.tipologia_id === filtroTipologia;
    return matchSearch && matchTipologia;
  });

  // Raggruppa per tipologia per visualizzazione
  // "Senza categoria" include anche i fornitori con tipologia_id ORFANA (tipologia cancellata).
  const tipologieIds = new Set(tipologie.map(t => t.id));
  const groupedItems = filtroTipologia === "all"
    ? tipologie.map(tip => ({
        tipologia: tip,
        fornitori: filteredItems.filter(f => f.tipologia_id === tip.id)
      })).concat([{
        tipologia: { id: null, nome: "Senza categoria", icona: "📋" },
        fornitori: filteredItems.filter(f => !f.tipologia_id || !tipologieIds.has(f.tipologia_id))
      }]).filter(g => g.fornitori.length > 0)
    : [{ tipologia: getTipologia(filtroTipologia) || { nome: "Fornitori" }, fornitori: filteredItems }];

  return (
    <div className="space-y-6" data-testid="fornitori-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="hidden md:block">
          <h1 className="text-3xl font-bold text-foreground">Fornitori</h1>
          <p className="text-muted-foreground mt-1">Gestisci i fornitori di merce</p>
        </div>
      </div>

      {/* Search + Filters + Add */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            placeholder="Cerca fornitore..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-14 pl-12 bg-card border-border text-foreground placeholder:text-muted-foreground rounded-2xl"
          />
        </div>
        
        {/* Filtro tipologia + Gestione Tipologie */}
        <div className="flex gap-2 items-center">
          <Select value={filtroTipologia} onValueChange={setFiltroTipologia}>
            <SelectTrigger className="w-full sm:w-48 h-14 bg-card border-border text-foreground rounded-2xl">
              <SelectValue placeholder="Tipologia" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="all" className="text-foreground">Tutte le tipologie</SelectItem>
              {tipologie.map(tip => (
                <SelectItem key={tip.id} value={tip.id} className="text-foreground">
                  {tip.icona} {tip.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Pulsante Gestione Tipologie - più visibile */}
          <button
            onClick={() => openTipologiaDialog()}
            className="h-14 w-14 sm:w-auto sm:px-4 bg-amber-100 hover:bg-amber-200 text-amber-900 rounded-2xl font-semibold flex items-center justify-center gap-2 transition-all border border-amber-300"
            title="Gestisci Tipologie"
            data-testid="gestione-tipologie-btn"
          >
            <Settings className="h-5 w-5" />
            <span className="hidden sm:inline">Tipologie</span>
          </button>

          {orfaniCount > 0 && (
            <button
              onClick={() => setOrfaniDialogOpen(true)}
              className="h-14 px-3 sm:px-4 rounded-2xl font-bold flex items-center gap-2 active:scale-[0.98] transition-colors bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200"
              title="Riassegna tipologia ai fornitori orfani"
              data-testid="btn-sistema-orfani-fornitori"
            >
              <Sparkles className="h-5 w-5" />
              <span className="hidden sm:inline">Sistema</span>
              <span
                className="text-xs font-mono rounded-full px-1.5 py-0.5"
                style={{ backgroundColor: '#4338CA', color: '#FFFFFF' }}
              >{orfaniCount}</span>
            </button>
          )}
        </div>
        
        <button 
          onClick={() => openDialog()}
          className="h-14 px-6 bg-primary text-white rounded-2xl font-bold flex items-center gap-2 active:scale-[0.98] transition-all"
        >
          <Plus className="h-5 w-5" />
          <span className="hidden sm:inline">Nuovo</span>
        </button>
      </div>

      {/* Lista Fornitori */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-white/5 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 bg-white/5 rounded-3xl backdrop-blur-sm border border-white/10">
          <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mb-4">
            <Truck className="h-10 w-10 text-white/30" />
          </div>
          <p className="text-white/50 text-center">
            {searchTerm ? "Nessun fornitore trovato" : "Nessun fornitore registrato"}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {groupedItems.map((group) => (
            <div key={group.tipologia?.id || "no-cat"}>
              {/* Header Tipologia */}
              {filtroTipologia === "all" && (
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">{group.tipologia?.icona}</span>
                  <h3 className="text-white/70 font-medium">{group.tipologia?.nome}</h3>
                  <span className="text-white/30 text-sm">({group.fornitori.length})</span>
                </div>
              )}
              
              {/* Fornitori */}
              <div className="space-y-3">
                {group.fornitori.map((item) => {
                  const tip = getTipologia(item.tipologia_id);
                  return (
                    <div
                      key={item.id}
                      className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10 hover:bg-white/10 transition-all"
                    >
                      <div className="flex items-start gap-4">
                        {/* Icon */}
                        <div className="w-12 h-12 bg-lime-400/20 rounded-xl flex items-center justify-center flex-shrink-0 text-2xl">
                          {tip?.icona || "📦"}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-white text-lg truncate">{item.ragione_sociale}</h3>
                            {item.categoria && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${item.categoria === "food" ? "bg-orange-500/20 text-orange-400" : item.categoria === "servizi" ? "bg-emerald-500/20 text-emerald-400" : "bg-blue-500/20 text-blue-400"}`}>
                                {item.categoria === "food" ? "🍽️ Food" : item.categoria === "servizi" ? "🔧 Servizi" : "📦 Packaging"}
                              </span>
                            )}
                            {tip && filtroTipologia !== "all" && (
                              <span className="text-xs px-2 py-0.5 bg-white/10 rounded-full text-white/50">
                                {tip.nome}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                            {item.piva && (
                              <span className="text-white/50 text-sm flex items-center gap-1">
                                P.IVA {item.piva}
                              </span>
                            )}
                            {item.contatto_nome && (
                              <span className="text-white/50 text-sm flex items-center gap-1">
                                <User className="h-3 w-3" /> {item.contatto_nome}
                              </span>
                            )}
                            {item.contatto_telefono && (
                              <a 
                                href={`tel:${item.contatto_telefono}`}
                                className="text-lime-400 text-sm flex items-center gap-1 hover:underline"
                              >
                                <Phone className="h-3 w-3" /> {item.contatto_telefono}
                              </a>
                            )}
                            {item.contatto_email && (
                              <a 
                                href={`mailto:${item.contatto_email}`}
                                className="text-white/50 text-sm flex items-center gap-1 hover:text-white"
                              >
                                <Mail className="h-3 w-3" /> {item.contatto_email}
                              </a>
                            )}
                          </div>
                          {/* Locali serviti */}
                          {item.locali_ids && item.locali_ids.length > 0 && locali && (
                            <div className="flex items-center gap-1 mt-2">
                              <MapPin className="h-3 w-3 text-blue-400" />
                              <span className="text-blue-400 text-xs">
                                {item.locali_ids.map(lid => locali.find(l => l.id === lid)?.nome).filter(Boolean).join(", ")}
                              </span>
                            </div>
                          )}
                          {item.note && (
                            <p className="text-white/40 text-xs mt-2 line-clamp-1">{item.note}</p>
                          )}
                          {/* Stats spesa */}
                          {(() => {
                            const s = spesaStats[item.id];
                            if (!s) return (
                              <div className="flex items-center gap-1 mt-2">
                                <TrendingUp className="h-3 w-3 text-white/20" />
                                <span className="text-white/25 text-xs">Nessun acquisto registrato</span>
                              </div>
                            );
                            const dataFormattata = s.ultimo_acquisto
                              ? new Date(s.ultimo_acquisto).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" })
                              : null;
                            return (
                              <div className="flex flex-wrap items-center gap-3 mt-2 pt-2 border-t border-white/10">
                                <span className="flex items-center gap-1 text-lime-400 font-semibold text-sm">
                                  <TrendingUp className="h-3.5 w-3.5" />
                                  €{s.totale_speso.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                                <span className="flex items-center gap-1 text-white/40 text-xs">
                                  <ShoppingCart className="h-3 w-3" />
                                  {s.num_acquisti} {s.num_acquisti === 1 ? "acquisto" : "acquisti"}
                                </span>
                                {dataFormattata && (
                                  <span className="flex items-center gap-1 text-white/40 text-xs">
                                    <CalendarDays className="h-3 w-3" />
                                    Ultimo: {dataFormattata}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-1 flex-shrink-0">
                          <button
                            onClick={() => navigate(`/acquisti/storico?fornitore_id=${item.id}`)}
                            className="p-2 rounded-xl hover:bg-white/10 active:bg-white/20 transition-colors"
                            title="Vedi tutte le fatture di questo fornitore"
                            data-testid={`vedi-fatture-fornitore-${item.id}`}
                          >
                            <FileText className="h-5 w-5 text-blue-400" />
                          </button>
                          <button
                            onClick={() => openDialog(item)}
                            className="p-2 rounded-xl hover:bg-white/10 active:bg-white/20 transition-colors"
                          >
                            <Pencil className="h-5 w-5 text-white/50" />
                          </button>
                          <button
                            onClick={() => setDeleteId(item.id)}
                            className="p-2 rounded-xl hover:bg-red-500/20 active:bg-red-500/30 transition-colors"
                          >
                            <Trash2 className="h-5 w-5 text-red-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog Fornitore */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md rounded-3xl bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Truck className="h-5 w-5 text-primary" />
              {editItem ? "Modifica Fornitore" : "Nuovo Fornitore"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Ragione Sociale *</Label>
              <Input
                value={form.ragione_sociale}
                onChange={(e) => setForm(prev => ({ ...prev, ragione_sociale: e.target.value }))}
                placeholder="es. Rossi S.r.l."
                className="h-12 bg-muted border-border text-foreground rounded-2xl"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground flex items-center gap-2">
                Partita IVA
                <span className="text-xs text-muted-foreground/60 font-normal">(auto-compila da VIES)</span>
              </Label>
              <div className="relative">
                <Input
                  value={form.piva}
                  onChange={(e) => setForm(prev => ({ ...prev, piva: e.target.value }))}
                  placeholder="es. 01234567890"
                  className="h-12 pr-28 bg-muted border-border text-foreground rounded-2xl"
                  data-testid="fornitore-piva-input"
                  disabled={viesLoading}
                />
                <button
                  type="button"
                  onClick={() => lookupPiva(form.piva)}
                  disabled={viesLoading || !form.piva}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 h-9 px-3 rounded-xl bg-primary/90 hover:bg-primary text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  data-testid="vies-lookup-btn"
                  title="Compila automaticamente da VIES"
                >
                  {viesLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  <span className="hidden sm:inline">{viesLoading ? "..." : "VIES"}</span>
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-muted-foreground">Categoria *</Label>
              <Select 
                value={form.categoria || "none"} 
                onValueChange={(v) => setForm(prev => ({ ...prev, categoria: v === "none" ? "" : v }))}
              >
                <SelectTrigger className="h-12 bg-muted border-border text-foreground rounded-2xl">
                  <SelectValue placeholder="Seleziona categoria" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="none" className="text-muted-foreground">Seleziona categoria</SelectItem>
                  <SelectItem value="food" className="text-foreground">🍽️ Food & Beverage</SelectItem>
                  <SelectItem value="packaging" className="text-foreground">📦 Attrezzature & Packaging</SelectItem>
                  <SelectItem value="servizi" className="text-foreground">🔧 Servizi & Utenze</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-muted-foreground">Tipologia</Label>
              <Select 
                value={form.tipologia_id || "none"} 
                onValueChange={(v) => setForm(prev => ({ ...prev, tipologia_id: v === "none" ? "" : v }))}
              >
                <SelectTrigger className="h-12 bg-muted border-border text-foreground rounded-2xl">
                  <SelectValue placeholder="Seleziona tipologia" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="none" className="text-muted-foreground">Nessuna tipologia</SelectItem>
                  {tipologie.map(tip => (
                    <SelectItem key={tip.id} value={tip.id} className="text-foreground">
                      {tip.icona} {tip.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-muted-foreground">Nome Contatto</Label>
              <Input
                value={form.contatto_nome}
                onChange={(e) => setForm(prev => ({ ...prev, contatto_nome: e.target.value }))}
                placeholder="es. Mario Rossi"
                className="h-12 bg-muted border-border text-foreground rounded-2xl"
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Telefono</Label>
                <Input
                  value={form.contatto_telefono}
                  onChange={(e) => setForm(prev => ({ ...prev, contatto_telefono: e.target.value }))}
                  placeholder="es. 333 1234567"
                  className="h-12 bg-muted border-border text-foreground rounded-2xl"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Email</Label>
                <Input
                  type="email"
                  value={form.contatto_email}
                  onChange={(e) => setForm(prev => ({ ...prev, contatto_email: e.target.value }))}
                  placeholder="es. info@rossi.it"
                  className="h-12 bg-muted border-border text-foreground rounded-2xl"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-muted-foreground">Note</Label>
              <Textarea
                value={form.note}
                onChange={(e) => setForm(prev => ({ ...prev, note: e.target.value }))}
                placeholder="Note aggiuntive..."
                className="bg-muted border-border text-foreground rounded-2xl min-h-[60px]"
              />
            </div>

            {/* Selezione Locali */}
            {locali && locali.length > 0 && (
              <div className="space-y-2">
                <Label className="text-muted-foreground flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Locali serviti
                </Label>
                <div className="grid grid-cols-1 gap-2 p-3 bg-muted rounded-2xl">
                  {locali.map(locale => (
                    <label 
                      key={locale.id} 
                      className="flex items-center gap-3 p-2 rounded-xl hover:bg-background/50 cursor-pointer"
                    >
                      <Checkbox
                        checked={form.locali_ids.includes(locale.id)}
                        onCheckedChange={() => toggleLocale(locale.id)}
                      />
                      <span className="text-foreground">{locale.nome}</span>
                    </label>
                  ))}
                </div>
                {form.locali_ids.length === 0 && (
                  <p className="text-xs text-muted-foreground">Se non selezioni nessun locale, il fornitore sarà visibile ovunque</p>
                )}
              </div>
            )}

            <DialogFooter className="gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 border-border text-foreground hover:bg-muted">
                Annulla
              </Button>
              <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90 text-white font-bold">
                {editItem ? "Salva" : "Crea"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Tipologie */}
      <Dialog open={tipologiaDialogOpen} onOpenChange={setTipologiaDialogOpen}>
        <DialogContent className="max-w-md rounded-3xl bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Tag className="h-5 w-5 text-primary" />
              {editTipologia ? "Modifica Tipologia" : "Gestione Tipologie"}
            </DialogTitle>
          </DialogHeader>
          
          {!editTipologia ? (
            <div className="space-y-4">
              {/* Lista tipologie esistenti */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {tipologie.map(tip => (
                  <div key={tip.id} className="flex items-center justify-between p-3 bg-muted rounded-xl">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{tip.icona}</span>
                      <span className="text-foreground">{tip.nome}</span>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => openTipologiaDialog(tip)}
                        className="p-1.5 rounded-lg hover:bg-muted"
                      >
                        <Pencil className="h-4 w-4 text-muted-foreground" />
                      </button>
                      <button
                        onClick={() => setDeleteTipologiaId(tip.id)}
                        className="p-1.5 rounded-lg hover:bg-red-500/20"
                      >
                        <Trash2 className="h-4 w-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Form nuova tipologia */}
              <div className="border-t border-border pt-4">
                <p className="text-muted-foreground text-sm mb-3">Aggiungi nuova tipologia</p>
                <form onSubmit={handleTipologiaSubmit} className="flex gap-2">
                  <Input
                    value={tipologiaForm.icona}
                    onChange={(e) => setTipologiaForm(prev => ({ ...prev, icona: e.target.value }))}
                    placeholder="📦"
                    className="w-16 h-12 bg-muted border-border text-foreground text-center rounded-xl"
                    maxLength={2}
                  />
                  <Input
                    value={tipologiaForm.nome}
                    onChange={(e) => setTipologiaForm(prev => ({ ...prev, nome: e.target.value }))}
                    placeholder="Nome tipologia"
                    className="flex-1 h-12 bg-muted border-border text-foreground rounded-xl"
                  />
                  <Button type="submit" className="h-12 px-4 bg-primary hover:bg-primary/90 text-white">
                    <Plus className="h-5 w-5" />
                  </Button>
                </form>
              </div>
            </div>
          ) : (
            <form onSubmit={handleTipologiaSubmit} className="space-y-4">
              <div className="flex gap-3">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Icona</Label>
                  <Input
                    value={tipologiaForm.icona}
                    onChange={(e) => setTipologiaForm(prev => ({ ...prev, icona: e.target.value }))}
                    className="w-20 h-12 bg-muted border-border text-foreground text-center rounded-xl text-xl"
                    maxLength={2}
                  />
                </div>
                <div className="flex-1 space-y-2">
                  <Label className="text-muted-foreground">Nome *</Label>
                  <Input
                    value={tipologiaForm.nome}
                    onChange={(e) => setTipologiaForm(prev => ({ ...prev, nome: e.target.value }))}
                    placeholder="Nome tipologia"
                    className="h-12 bg-muted border-border text-foreground rounded-xl"
                  />
                </div>
              </div>
              <DialogFooter className="gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditTipologia(null)} className="flex-1 border-border text-foreground hover:bg-muted">
                  Indietro
                </Button>
                <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90 text-white font-bold">
                  Salva
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Fornitore */}
      <DeleteConfirmDialog 
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Elimina Fornitore"
        message="Vuoi eliminare questo fornitore? L'operazione non può essere annullata."
      />
      
      {/* Delete Confirm Tipologia */}
      <DeleteConfirmDialog 
        open={!!deleteTipologiaId}
        onClose={() => setDeleteTipologiaId(null)}
        onConfirm={handleDeleteTipologia}
        title="Elimina Tipologia"
        message="Vuoi eliminare questa tipologia? I fornitori associati rimarranno senza categoria."
      />

      {/* Dialog Sistema Orfani */}
      <SistemaOrfaniDialog
        open={orfaniDialogOpen}
        onClose={() => setOrfaniDialogOpen(false)}
        mode="fornitori"
        getAuthHeaders={getAuthHeaders}
        onSaved={() => { fetchData(); fetchOrfaniCount(); }}
      />
    </div>
  );
};

export default Fornitori;
